import { ApiError } from "../../api";
import { getPlan as fetchPlan, getSong as fetchSong, messageOf } from "../songs/music-api";
import { getPlan as readPlan, getSong as readSong, putSong } from "./cache";
import { getOfflineReadiness } from "./download";
import { practiceFrom } from "./practice";
import type { PlanDetail } from "../../../shared/types";

export interface StandLoad {
  plan: PlanDetail;
  practice: boolean;
  offline: boolean;
}

export async function loadStand(routeId: string): Promise<StandLoad> {
  try {
    const plan = await fetchPlan(routeId);
    return { plan, practice: false, offline: false };
  } catch (caught) {
    const status = caught instanceof ApiError ? caught.status : 0;
    if (status === 404) {
      try {
        const song = await fetchSong(routeId);
        await putSong(song).catch(() => undefined);
        const plan = practiceFrom(song);
        return { plan, practice: true, offline: false };
      } catch (songError) {
        if (songError instanceof ApiError && songError.status !== 404) throw songError;
      }
    }
    if (status === 401) throw new Error("Sign in, then open the stand again.");
    if (status === 404) throw new Error("This plan is not on this device yet. Open it once while you are connected.");
    if (status > 0) throw new Error(messageOf(caught));
    const cachedPlan = await readPlan(routeId).catch(() => null);
    if (cachedPlan) {
      if ((await getOfflineReadiness(cachedPlan).catch(() => null))?.ready) {
        return { plan: cachedPlan, practice: cachedPlan.serviceTypeName === "Practice", offline: true };
      }
      throw new Error("This set was not fully downloaded. Reconnect and use Download set before performing offline.");
    }
    const cachedSong = await readSong(routeId).catch(() => null);
    if (cachedSong) {
      const practice = practiceFrom(cachedSong);
      if ((await getOfflineReadiness(practice).catch(() => null))?.ready) return { plan: practice, practice: true, offline: true };
      throw new Error("This song was not fully downloaded. Reconnect and use Download set before practising offline.");
    }
    throw new Error(messageOf(caught));
  }
}
